import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import TableCard from "./widgets/TableCard";
import { useTenantUsers } from "../hooks";
import styles from "./UserManagement.module.css";

const STATUS_LABEL = {
    APPROVED: "승인",
    WAITING: "대기",
    PENDING: "요청",
    LOCKED: "잠금",
    REJECTED: "거절",
    DEACTIVATED: "비활성",
};
const STATUS_ORDER = [
    "APPROVED",
    "WAITING",
    "PENDING",
    "LOCKED",
    "REJECTED",
    "DEACTIVATED",
];

function StatusBadge({ code }) {
    const label = STATUS_LABEL[code] || code;
    const cls =
        code === "PENDING" ? styles.badgePending :
            code === "APPROVED" ? styles.badgeApproved :
                code === "REJECTED" ? styles.badgeRejected :
                    code === "LOCKED" ? styles.badgeLocked :
                        code === "DEACTIVATED" ? styles.badgeDeactivated :
                            styles.badgeWaiting;
    return <span className={`${styles.badge} ${cls}`}>{label}</span>;
}

function StatusChangeModal({ open, onClose, onConfirm, count, currentStatus }) {
    const [nextStatus, setNextStatus] = useState(currentStatus || "APPROVED");
    const [err, setErr] = useState("");

    useEffect(() => {
        if (open) {
            setNextStatus(currentStatus || "APPROVED");
            setErr("");
        }
    }, [open, currentStatus]);

    if (!open) return null;

    return (
        <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h3>사용자 상태 변경</h3>
                    <button className={styles.iconBtn} onClick={onClose}>✕</button>
                </div>

                <div className={styles.modalBody}>
                    <p className={styles.modalText}>선택된 {count}명의 상태를 변경합니다.</p>

                    <label className={styles.field}>
                        <span className={styles.label}>새 상태</span>
                        <select
                            className={styles.select}
                            value={nextStatus}
                            onChange={(e) => setNextStatus(e.target.value)}
                        >
                            {STATUS_ORDER.map((code) => (
                                <option key={code} value={code}>
                                    {STATUS_LABEL[code]}
                                </option>
                            ))}
                        </select>
                    </label>

                    {err && <p className={styles.error}>{err}</p>}

                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.btnGhost} onClick={onClose}>
                            취소
                        </button>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={async () => {
                                if (!nextStatus) {
                                    setErr("상태를 선택해주세요.");
                                    return;
                                }
                                setErr("");
                                await onConfirm(nextStatus);
                            }}
                        >
                            변경
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const USER_LIST_MAX_ROWS = 20;
const USER_LIST_COLUMNS = [
    { key: "check", header: "", width: "50px", type: "check", align: "center" },
    { key: "name", header: "이름", width: "200px", align: "center", search: true },
    { key: "email", header: "이메일", width: "350px", align: "center" },
    { key: "count", header: "서비스 사용횟수", width: "1fr", align: "center" },
    { key: "created_at", header: "가입일시", width: "400px", align: "center" },
    {
        key: "status",
        header: "상태",
        width: "200px",
        align: "center",
        accessor: (row) => <StatusBadge code={row.status} />,
    },
];

export default function UserManagement() {
    const { user } = useAuth();

    // 🔹 검색 인풋에 바인딩된 값 (타이핑 중인 값)
    const [selectUserName, setSelectUserName] = useState("");

    // 🔹 실제 API에 사용하는 검색어
    const [searchUserName, setSearchUserName] = useState("");

    // 🔹 상태 필터, 페이지
    const [selectUserStatus, setSelectUserStatus] = useState("");
    const [selectUserPage, setSelectUserPage] = useState(1);

    // 🔹 마지막으로 성공한 결과를 보존
    const [tenantUsers, setTenantUsers] = useState(null);

    // 🔹 로딩 플래그
    const [isLoading, setIsLoading] = useState(false);

    // useTenantUsers 훅에서 refetch만 사용 (자동 fetch는 우리가 직접 제어)
    const { refetch } = useTenantUsers(
        user?.tenant_id,
        searchUserName,
        selectUserStatus,
        selectUserPage,
        USER_LIST_MAX_ROWS
    );

    // 공통 fetch 함수
    async function fetchAndApply() {
        setIsLoading(true);
        try {
            const { data } = await refetch();
            if (data) setTenantUsers(data);
        } finally {
            setIsLoading(false);
        }
    }

    // ✅ 1) 페이지나 상태 바뀌면 새로 가져오기
    useEffect(() => {
        fetchAndApply();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectUserPage, selectUserStatus, searchUserName, user?.tenant_id]);

    // ✅ 2) 엔터 또는 🔍 버튼 → 의도적으로 검색 실행
    const handleExplicitSearch = async () => {
        // 검색 확정
        setSearchUserName(selectUserName);
        // 페이지를 1페이지로 초기화 (보통 검색 시 첫 페이지로 가는 UX)
        setSelectUserPage(1);
    };

    // ✅ 3) 🔥 사용자가 검색어를 전부 지웠을 때 (""), 엔터 없이 자동으로 전체 목록으로 복귀
    const handleSearchInputChange = (e) => {
        const nextValue = e.target.value;
        setSelectUserName(nextValue);

        if (nextValue === "") {
            // 전체검색 모드로 전환
            setSearchUserName("");   // 실제 API 검색어도 비움
            setSelectUserPage(1);    // 첫 페이지로
            // fetchAndApply()는 위의 useEffect가 searchUserName 변경을 감지해서 자동 호출
        }
    };

    // ✅ 엔터 처리
    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            handleExplicitSearch();
        }
    };

    // ✅ 검색 버튼 처리
    const handleSearchClick = () => {
        handleExplicitSearch();
    };

    // ----------------------
    // 선택(체크박스)
    // ----------------------
    const [selected, setSelected] = useState(new Set());

    const toggleOne = (k) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(k) ? next.delete(k) : next.add(k);
            return next;
        });
    };

    const toggleAll = (visibleKeys) => {
        setSelected((prev) => {
            const next = new Set(prev);
            const allSelected = visibleKeys.every((k) => next.has(k));
            if (allSelected) {
                visibleKeys.forEach((k) => next.delete(k));
            } else {
                visibleKeys.forEach((k) => next.add(k));
            }
            return next;
        });
    };

    // ----------------------
    // 상태 변경 모달
    // ----------------------
    const [statusModalOpen, setStatusModalOpen] = useState(false);

    const openStatusModal = () => {
        if (selected.size) {
            setStatusModalOpen(true);
        }
    };

    const confirmStatusChange = async (nextStatus) => {
        setSelected(new Set());
        setStatusModalOpen(false);
        alert("상태가 변경되었습니다.");
    };

    const handleBulkDelete = async () => {
        if (!selected.size) return;
        if (!window.confirm(`선택한 ${selected.size}명을 삭제할까요?`)) return;
        setSelected(new Set());
        alert("삭제 완료");
    };

    return (
        <div className={styles.wrap}>
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>사용자 관리</h2>

                    <div className={styles.tools}>
                        <div className={styles.searchBox}>
                            <input
                                type="search"
                                placeholder="검색"
                                value={selectUserName}
                                onChange={handleSearchInputChange}   // 🔥 변경된 핸들러
                                onKeyDown={handleSearchKeyDown}      // 엔터 핸들러
                                aria-label="사용자 검색"
                            />
                            <button
                                className={styles.iconBtn}
                                onClick={handleSearchClick}          // 🔍 클릭 시 검색 확정
                                aria-label="검색"
                            >
                                🔍
                            </button>
                        </div>

                        <select
                            className={styles.select}
                            value={selectUserStatus}
                            onChange={(e) => {
                                setSelectUserStatus(e.target.value);
                                setSelectUserPage(1);
                            }}
                            aria-label="상태 필터"
                            title="상태 필터"
                        >
                            <option value="">전체 상태</option>
                            {STATUS_ORDER.map((code) => (
                                <option key={code} value={code}>
                                    {STATUS_LABEL[code]}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <TableCard
                    data={tenantUsers?.items || []}
                    columns={USER_LIST_COLUMNS}
                    rows={USER_LIST_MAX_ROWS}
                    pagenateSize={USER_LIST_MAX_ROWS}
                    page={selectUserPage}
                    total={tenantUsers?.total || 0}
                    className={styles.cardService}
                    onPageChange={setSelectUserPage}
                    selectedKeys={selected}
                    onToggleOne={toggleOne}
                    onToggleAll={toggleAll}
                    rowKeyField="key"
                    loading={isLoading}
                />

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.btnPrimary}
                        onClick={openStatusModal}
                        disabled={!selected.size}
                        title="선택 항목 상태 변경"
                    >
                        사용자 상태변경 ✏️
                    </button>
                    <button
                        type="button"
                        className={styles.btnGhost}
                        onClick={handleBulkDelete}
                        disabled={!selected.size}
                        title="선택 항목 삭제"
                    >
                        사용자 삭제 🗑️
                    </button>
                </div>
            </div>

            <StatusChangeModal
                open={statusModalOpen}
                onClose={() => setStatusModalOpen(false)}
                onConfirm={confirmStatusChange}
                count={selected.size}
                currentStatus={null}
            />
        </div>
    );
}
